import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import AccountCircle from "@material-ui/icons/AccountCircle";
import InputAdornment from "@material-ui/core/InputAdornment";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import HomeIcon from "@material-ui/icons/Home";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import EmailIcon from "@material-ui/icons/Email";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import DoaPic from "../assets/doa.jpg";
import FitrahCalculator from "./FitrahCalculator";
import AkadFitrah from "./AkadFitrah";
import ZakatTextField from "./ZakatTextField";
import type { State } from "./rules";
import { Fields, stateList } from "./rules";
import {
  generateLink,
  validateAddress,
  validateIC,
  validateName,
  validatePhone,
  validatePostcode,
  validateState,
} from "./utils";
import type { Payer } from "./utils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    backButton: {
      marginRight: theme.spacing(1),
    },
    instructions: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },

    total: {
      justifyContent: "center",
      padding: 20,
    },
    grid: {
      alignItems: "center",
      justifyContent: "center",
      justifyItems: "center",
      marginBottom: 50,

      display: "flex",
    },
    image: {
      width: theme.spacing(20),
      alignSelf: "center",
      justifyContent: "center",
    },
    gridRoot: {
      padding: 30,
    },
    button: {
      justifyContent: "center",
      width: "100%",
    },
    gridTypo: {
      display: "flex",
      justifyContent: "center",
      marginBottom: theme.spacing(5),
    },
    paper: {
      alignItems: "center",
      justify: "center",
      // width: theme.spacing(20),
      width: "100%",
      height: theme.spacing(8),
    },
    typo: {
      fontWeight: "bold",
      display: "flex",
      textAlign: "center",
      alignItems: "center",
      justifyContent: "center",
      height: theme.spacing(8),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    formControl: {
      marginTop: -7,
    },
    pic: {
      width: "100%",
    },
  })
);

interface Props {
  body: string;
  state: State;
  payer_email: string;
  amil?: string;
  kariah?: string;
}

interface Form {
  payer: Payer;
  daerah_list: string[];
}

const defaultForm = ({
  body,
  payer_email,
  state,
  amil,
  kariah,
}: Props): Form => {
  const default_rate = state.fitrah_rates[0];
  const default_state = state.state;

  let default_daerah_list: string[] = [];
  let default_daerah: string | undefined = undefined;

  if (state.hasField(Fields.DaerahText)) {
    default_daerah = "";
  } else if (state.hasField(Fields.DaerahSelect)) {
    if (state.daerah_list) {
      let found = false;
      if (state.daerah_list[default_state]) {
        found = true;
        default_daerah_list = state.daerah_list[default_state];
        default_daerah = "";
      }

      if (!found) {
        if (state.daerah_list.default) {
          default_daerah_list = state.daerah_list.default;
          default_daerah = default_daerah_list[0];
        }
      }

      if (!default_daerah) {
        default_daerah = "";
      }
    }
  }

  const payer: Payer = {
    amil: amil,
    kariah_code: kariah,
    email: payer_email,
    dependant: 1,
    rate: default_rate,
    amount: default_rate,
    zakat_code: state.zakat_code,
    state: default_state,
    daerah: default_daerah,
  };

  if (state.hasField(Fields.Name)) {
    payer.name = "";
  }

  if (state.hasField(Fields.IC)) {
    payer.ic_no = "";
  }

  if (state.hasField(Fields.Phone)) {
    payer.phone = "";
  }

  if (state.hasField(Fields.Address)) {
    payer.address = "";
  }

  if (state.hasField(Fields.Address2)) {
    payer.address_2 = "";
  }

  if (state.hasField(Fields.City)) {
    payer.city = "";
  }

  if (state.hasField(Fields.Postcode)) {
    payer.postcode = "";
  }

  return {
    payer,
    daerah_list: default_daerah_list,
  };
};

type Action =
  | { type: "update_number"; rate: number; dependant: number; amount: number }
  | { type: "update_payer"; payer: Payer }
  | {
      type: "update_state";
      state: string;
      daerah: string | undefined;
      daerah_list: string[];
    };

const reducer = (form: Form, action: Action): Form => {
  switch (action.type) {
    case "update_number": {
      const { payer, daerah_list } = form;
      const { rate, dependant, amount } = action;

      return {
        payer: {
          ...payer,
          rate,
          dependant,
          amount,
        },
        daerah_list,
      };
    }
    case "update_payer": {
      return {
        ...form,
        payer: action.payer,
      };
    }
    case "update_state": {
      const { payer } = form;
      const { state, daerah, daerah_list } = action;
      return {
        payer: { ...payer, state, daerah },
        daerah_list,
      };
    }
    default:
      throw new Error("unknown action type");
  }
};

const FitrahForm = ({ body, payer_email, state, amil, kariah }: Props) => {
  const classes = useStyles();

  const [form, dispatch] = React.useReducer(
    reducer,
    defaultForm({ body, payer_email, state, amil, kariah })
  );

  const { payer, daerah_list } = form;

  const setPayer = (data: Payer) => {
    dispatch({
      type: "update_payer",
      payer: data,
    });
  };

  const updateState = (value: string) => {
    if (state.hasField(Fields.DaerahSelect)) {
      if (state.daerah_list) {
        let found = false;
        if (state.daerah_list[value]) {
          found = true;
          dispatch({
            type: "update_state",
            state: value,
            daerah: "",
            daerah_list: state.daerah_list[value],
          });
        }

        if (!found) {
          if (state.daerah_list.default) {
            dispatch({
              type: "update_state",
              state: value,
              daerah: state.daerah_list.default[0],
              daerah_list: state.daerah_list.default,
            });
          }
        }
      }
    } else {
      let daerah: string | undefined = undefined;
      if (state.hasField(Fields.DaerahText)) {
        daerah = "";
      }

      dispatch({
        type: "update_state",
        state: value,
        daerah,
        daerah_list: [],
      });
    }
  };

  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const updateNumber = (dependant: number, rate: number, amount: number) => {
    dispatch({ type: "update_number", dependant, rate, amount });
  };

  // const onChange = (field: string) => (
  //   evt: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   setPayer({ ...payer, [field]: evt.target.value });
  // };

  const updateDaerah = (evt: React.ChangeEvent<{ value: unknown }>) => {
    const value = evt.target.value as string;
    setPayer({ ...payer, daerah: value });
  };

  const onStateChange = (evt: React.ChangeEvent<{ value: unknown }>) => {
    const value = evt.target.value as string;
    updateState(value);
  };

  const updateField = (key: string) => (value: string) => {
    setPayer({ ...payer, [key]: value });
  };

  // TODO move the validation to elsewhere
  const handleSubmit = (
    evt: React.MouseEvent<HTMLButtonElement | MouseEvent>
  ) => {
    evt.preventDefault();

    if (!payer.email) {
      alert("Sila pastikan alamat emel anda betul");
      return;
    }

    if (payer.rate < state.fitrah_rates[0]) {
      alert(`Sila pastikan kadar fitrah melebihi RM${state.fitrah_rates[0]}`);
      return;
    }

    if (payer.dependant <= 0) {
      alert("Sila pastikan sekurang-kurangnya seorang tanggungan");
      return;
    }

    if (payer.amount <= 0) {
      alert("Sila pastikan jumlah yang betul");
      return;
    }

    if (state.hasField(Fields.Name)) {
      if (!payer.name) {
        alert("Sila isikan nama");
        return;
      }

      const err = validateName(payer.name);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.Phone)) {
      if (!payer.phone) {
        alert("Sila isikan nombor telefon");
        return;
      }

      const err = validatePhone(payer.phone);
      console.log(payer.phone, err);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.IC)) {
      if (!payer.ic_no) {
        alert("Sila isikan nombor kp");
        return;
      }

      const err = validateIC(payer.ic_no);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.Address)) {
      if (!payer.address) {
        alert("Sila isikan alamat");
        return;
      }

      const err = validateAddress("alamat")(payer.address);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.Address2)) {
      if (!payer.address_2) {
        alert("Sila isikan alamat 2");
        return;
      }

      const err = validateAddress("alamat 2")(payer.address_2);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.City)) {
      if (!payer.city) {
        alert("Sila isikan bandar");
        return;
      }
    }

    if (
      state.hasField(Fields.DaerahSelect) ||
      state.hasField(Fields.DaerahText)
    ) {
      if (!payer.daerah) {
        alert("Sila pilihkan daerah");
        return;
      }
    }

    if (state.hasField(Fields.Postcode)) {
      if (!payer.postcode) {
        alert("Sila isikan nombor poskod");
        return;
      }

      const err = validatePostcode(payer.postcode);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    if (state.hasField(Fields.State)) {
      if (!payer.state) {
        alert("Sila pilihkan negeri");
        return;
      }

      const err = validateState(payer.state);
      if (err !== null) {
        alert(err.message);
        return;
      }
    }

    proceed();
  };

  const proceed = () => {
    if (state.akad) {
      if (open) {
        generateLink(body, payer, state.extras_func);
      } else {
        setOpen(true);
      }
    } else {
      generateLink(body, payer, state.extras_func);
    }
  };

  const logo = require(`../assets/body/${state.logo}`);

  return (
    <React.Fragment>
      <Grid className={classes.gridRoot}>
        <Grid item xs={12} className={classes.grid}>
          <img src={logo} alt="logo" className={classes.image} />;
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={12} className={classes.gridTypo}>
          <Paper elevation={4} className={classes.paper}>
            <Typography className={classes.typo}>
              Jumlah Dibayar: RM {payer.amount}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {state.hasField(Fields.Name) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="name"
                  label="Nama"
                  required
                  value={payer.name ? payer.name : ""}
                  validator={validateName}
                  onChange={updateField("name")}
                  icon={<AccountCircle />}
                />
                {/* <TextField
                  required
                  id="name"
                  name="name"
                  label="Nama"
                  fullWidth
                  onChange={onChange("name")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.IC) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  type="number"
                  id="ic_no"
                  label="Nombor Kad Pengenalan"
                  required
                  value={payer.ic_no ? payer.ic_no : ""}
                  validator={validateIC}
                  onChange={updateField("ic_no")}
                  icon={<FingerprintIcon />}
                />
                {/* <TextField
                  required
                  id="ic_no"
                  name="ic_no"
                  label="Nombor Kad Pengenalan"
                  fullWidth
                  onChange={onChange("ic_no")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FingerprintIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.Phone) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  type="number"
                  id="phone"
                  label="Nombor Telefon"
                  required
                  value={payer.phone ? payer.phone : ""}
                  validator={validatePhone}
                  onChange={updateField("phone")}
                  icon={<PhoneIphoneIcon />}
                />
                {/* <TextField
                  required
                  type="number"
                  id="phone"
                  name="phone"
                  label="Nombor Telefon"
                  fullWidth
                  onChange={onChange("phone")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIphoneIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.Address) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="address"
                  label="Alamat"
                  required
                  value={payer.address ? payer.address : ""}
                  validator={validateAddress("alamat")}
                  onChange={updateField("address")}
                  icon={<HomeIcon />}
                />
                {/* <TextField
                  required
                  id="address"
                  name="address"
                  label="Alamat"
                  fullWidth
                  onChange={onChange("address")}
                  value={payer.address?.toUpperCase()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.Address2) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="address_2"
                  label="Alamat 2"
                  required
                  value={payer.address_2 ? payer.address_2 : ""}
                  validator={validateAddress("alamat 2")}
                  onChange={updateField("address_2")}
                  icon={<HomeIcon />}
                />
                {/* <TextField
                  required
                  id="address_2"
                  name="address_2"
                  label="Alamat 2"
                  fullWidth
                  onChange={onChange("address_2")}
                  value={payer.address_2?.toUpperCase()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.Address3) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="address_3"
                  label="Alamat 3"
                  required
                  value={payer.address_3 ? payer.address_3 : ""}
                  validator={validateAddress("alamat 3")}
                  onChange={updateField("address_3")}
                  icon={<HomeIcon />}
                />
                {/* <TextField
                  required
                  id="address_2"
                  name="address_2"
                  label="Alamat 2"
                  fullWidth
                  onChange={onChange("address_2")}
                  value={payer.address_2?.toUpperCase()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.City) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="city"
                  label="Bandar"
                  required
                  value={payer.city ? payer.city : ""}
                  onChange={updateField("city")}
                  icon={<LocationCityIcon />}
                />
                {/* <TextField
                  required
                  id="city"
                  name="city"
                  label="Bandar"
                  fullWidth
                  onChange={onChange("city")}
                  value={payer.city?.toUpperCase()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.Postcode) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  type="number"
                  id="postcode"
                  label="Poskod"
                  required
                  value={payer.postcode ? payer.postcode : ""}
                  validator={validatePostcode}
                  onChange={updateField("postcode")}
                  icon={<LocationCityIcon />}
                />
                {/* <TextField
                  required
                  id="postcode"
                  name="postcode"
                  label="Poskod"
                  type="number"
                  fullWidth
                  onChange={onChange("postcode")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.DaerahText) && (
              <Grid item xs={12} sm={6}>
                <ZakatTextField
                  id="daerah"
                  label="Daerah"
                  required
                  value={payer.daerah ? payer.daerah : ""}
                  onChange={updateField("daerah")}
                  icon={<LocationCityIcon />}
                />
                {/* <TextField
                  required
                  id="daerah"
                  name="daerah"
                  label="Daerah"
                  fullWidth
                  onChange={onChange("daerah")}
                  value={payer.daerah?.toUpperCase()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon />
                      </InputAdornment>
                    ),
                  }}
                /> */}
              </Grid>
            )}

            {state.hasField(Fields.DaerahSelect) && daerah_list && (
              <Grid item xs={12} sm={6} className={classes.formControl}>
                <FormControl fullWidth>
                  <FormHelperText>Daerah</FormHelperText>
                  <Select
                    value={payer.daerah}
                    fullWidth
                    onChange={updateDaerah}
                  >
                    {daerah_list.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {state.hasField(Fields.State) && (
              <Grid item xs={12} sm={6} className={classes.formControl}>
                <FormControl fullWidth>
                  <FormHelperText>Negeri</FormHelperText>
                  <Select
                    value={payer.state}
                    fullWidth
                    onChange={onStateChange}
                  >
                    {stateList.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} className={classes.formControl}>
              <TextField
                required
                id="email"
                name="email"
                label="Emel"
                fullWidth
                variant="filled"
                disabled
                defaultValue={payer_email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FitrahCalculator
            custom={state.hasField(Fields.FitrahRateCustom)}
            rates={state.fitrah_rates}
            update={updateNumber}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.button}>
        <Button
          type="button"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={handleSubmit}
        >
          Bayar
        </Button>
      </Grid>

      {state.akad && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Akad Fitrah</DialogTitle>
          <DialogContent dividers>
            <AkadFitrah simple={state.simple_akad} amount={payer.amount} />
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={handleClose} color="secondary">
              Batal
            </Button>
            <Button autoFocus onClick={proceed} color="primary">
              Bayar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* <Dialog open>
        <DialogTitle>Zakat Fitrah 1441 Hijrah</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Bayaran Zakat Fitrah 1441 Hijrah bermula pada 1 Ramadhan (selepas
            waktu maghrib) sehingga 1 Syawal (sebelum 8 pagi)
          </Typography>
        </DialogContent>
      </Dialog> */}

      {state.doa && (
        <Grid item xs={12}>
          <img src={DoaPic} alt="doa" className={classes.pic} />
        </Grid>
      )}
    </React.Fragment>
  );
};

export default FitrahForm;
